import {
  BoxGeometry,
  CylinderGeometry,
  Color,
  Group,
  type Material,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  PointLight,
  SphereGeometry,
  TorusGeometry,
} from 'three';
import { EMIT, SURFACE } from '../render/palette';
import {
  MATERIALS,
  applyContactShading,
  emitter,
  flatVertexColor,
  glow,
  makeTvTexture,
  signMaterial,
  surface,
  tintVertexColor,
} from '../render/materials';
import type { StoreId } from '../sim/types';
import { lightPool, lightWash, mannequin } from './props';

/**
 * PER-STORE DRESSING.
 *
 * `placeContainers` in plan.ts gives every unit the same shelves, cabinet and
 * counter, because those are the things the sim reasons about — they are loot
 * containers with locks and reach radii. This file is everything the sim does
 * NOT care about: the stuff that makes a jeweller look like a jeweller and a
 * gun shop look like a gun shop.
 *
 * That split matters. Nothing here is interactive, nothing here registers with
 * the sim, and nothing here casts a collider — it is pure set dressing, so it
 * can be as dense as the frame rate allows without touching gameplay.
 *
 * LOCAL FRAME, shared by every builder below:
 *   - x spans [-width/2, +width/2], the shopfront width.
 *   - z = 0 is the shopfront line; -depth is the back wall. Deeper is more negative.
 *   - y = 0 is the shop floor.
 * This is the same frame `placeContainers` uses, so the two can be read side by
 * side when deciding where something goes without colliding with a container.
 */

const box = (w: number, h: number, d: number, mat: Material, contact = true) => {
  const geo = flatVertexColor(new BoxGeometry(w, h, d));
  if (contact) applyContactShading(geo);
  const m = new Mesh(geo, mat);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
};

/** Position a node and return it, so a mesh can be built and placed in one expression. */
const at = <T extends { position: { set(x: number, y: number, z: number): void } }>(
  node: T,
  x: number,
  y: number,
  z: number,
): T => {
  node.position.set(x, y, z);
  return node;
};

/**
 * Coloured card — product, packaging, fabric. The cheapest way to fill a shelf,
 * and the reason the stores can be this dense: every box of pills, coat, shoe
 * and cereal packet in the mall is one of these.
 *
 * The colour rides in the geometry, not in a cloned material. Cloning per item
 * was the obvious way to write this and it cost the supermarket three hundred
 * and thirty-three materials for four hundred and forty objects — none of which
 * could ever be batched, because batching is by material. Tinting the vertices
 * gives the identical picture and lets a whole store merge into a few draws.
 * See `tintVertexColor`.
 */
const card = (w: number, h: number, d: number, hexColor: number) => {
  const geo = tintVertexColor(flatVertexColor(new BoxGeometry(w, h, d)), hexColor);
  const m = new Mesh(geo, MATERIALS.card());
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
};

/** Small accent light. Budgeted: at most two per store, or the shader cost bites. */
function accent(color: Color, intensity: number, distance: number) {
  return new PointLight(color, intensity, distance, 2);
}

/** Wall-mounted flat sign facing +z (out toward the concourse). */
function wallSign(text: string, w: number, h: number, color: Color, font: 'condensed' | 'wide' | 'handwritten' = 'condensed') {
  const m = new Mesh(
    new PlaneGeometry(w, h),
    signMaterial({ text, color, widthMetres: w, heightMetres: h, font, align: 'center' }),
  );
  return m;
}

// ---------------------------------------------------------------------------
// ETERNAL YOU — jewellery. The mascot store, so it gets the most work.
// ---------------------------------------------------------------------------

function dressJewellery(width: number, depth: number, _height: number): Group {
  const g = new Group();

  /**
   * A horseshoe of lit cases. Jewellery retail is the one shop type where the
   * fittings ARE the product display — there is no shelf of boxes, just glass
   * and light — so the room has to be built out of cases or it reads as empty.
   */
  const CASE_H = 1.02;
  const caseRun = (cx: number, cz: number, len: number, yaw: number) => {
    const c = new Group();
    c.position.set(cx, 0, cz);
    c.rotation.y = yaw;

    // Plinth, glass box, and a bright interior pad. The pad is what you see
    // from across the concourse — a row of warm slots floating in a dark room.
    c.add(at(box(len, 0.78, 0.62, MATERIALS.plasterDim()), 0, 0.39, 0));

    const pad = new Mesh(
      new PlaneGeometry(len - 0.1, 0.5),
      emitter('vitrinePad', EMIT.shopWarm, 0.22),
    );
    pad.rotation.x = -Math.PI / 2;
    pad.position.y = 0.8;
    c.add(pad);

    const glassBox = box(len, CASE_H - 0.78, 0.62, MATERIALS.glass(), false);
    glassBox.position.y = 0.78 + (CASE_H - 0.78) / 2;
    glassBox.castShadow = false;
    c.add(glassBox);

    /**
     * The goods, at true scale. A ring is a two-centimetre hoop, not a nine —
     * the first pass drew them at 0.045 radius and a row of them read as a line
     * of gold doughnuts sitting on the counter. Being almost too small to see is
     * correct: what you steal here fits in a fist, and the case light doing the
     * work is what tells you there is something in there worth taking.
     *
     * Each ring sits on a small dark velvet pad, which is what actually makes it
     * legible — a bare gold speck on a bright pad disappears; the same speck on
     * a dark square is a jewel.
     */
    const goods = emitter('gold', new Color().setHex(0xffd98a, 'srgb'), 1.4);
    const velvet = MATERIALS.velvet();
    const n = Math.max(3, Math.round(len / 0.34));
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const x = -len / 2 + t * len;
      const z = i % 2 ? 0.11 : -0.11;
      c.add(at(box(0.09, 0.01, 0.09, velvet, false), x, 0.806, z));
      const ring = new Mesh(new TorusGeometry(0.02, 0.006, 4, 10), goods);
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(x, 0.818, z);
      c.add(ring);
    }
    return c;
  };

  const inset = 0.9;
  g.add(caseRun(0, -depth + inset, width - 2.4, 0));
  g.add(caseRun(-width / 2 + inset, -depth / 2 - 0.4, depth - 2.2, Math.PI / 2));
  g.add(caseRun(width / 2 - inset, -depth / 2 - 0.4, depth - 2.2, Math.PI / 2));

  /**
   * Floor spill from the cases, as flat washes rather than more light.
   *
   * A third of this frame was black and almost all of it was floor and case
   * fronts — the room was three bright strips of counter-top hanging in a void.
   * A lit vitrine does throw light down its own front onto the tiles, so this is
   * the honest fix and not a cheat, and it costs nothing: two accent lights is
   * the whole budget this store is allowed before the back wall hazes over.
   */
  const spill = (cx: number, cz: number, len: number, yaw: number) => {
    const w = lightWash(len + 1.6, 3.2, EMIT.shopWarm, 0.24, 'vitrine-floor');
    w.rotation.x = -Math.PI / 2;
    w.rotation.z = -yaw;
    // 0.035, not 0.015: the shop band lays a floor overlay at y = 0.02 and the first
    // pass put this underneath it, where it rendered nothing at all.
    w.position.set(cx, 0.035, cz);
    return w;
  };
  g.add(spill(0, -depth + inset + 1.1, width - 2.4, 0));
  g.add(spill(-width / 2 + inset + 1.1, -depth / 2 - 0.4, depth - 2.2, Math.PI / 2));
  g.add(spill(width / 2 - inset - 1.1, -depth / 2 - 0.4, depth - 2.2, Math.PI / 2));

  /**
   * Backlit niches above the rear case.
   *
   * These were mirrors on the first pass, on the reasoning that a jeweller's
   * back wall is always mirror. They read as three flat grey rectangles: a real
   * mirror needs a reflection probe, and there is nothing in a dark room for a
   * fake one to reflect. Lit alcoves solve the same problem better — the back
   * wall still has three bright vertical shapes on it, and in an ink-black shop
   * they are the thing you see from the concourse.
   *
   * The x positions are the GAPS between the two back-wall stock shelves that
   * `placeContainers` puts in every store. Those shelves are loot containers and
   * cannot move, and on the first pass the niches sat directly behind them and
   * were invisible. If that shelf layout changes, this has to follow.
   */
  const shelfW = Math.min(2.0, width * 0.42);
  const shelfInner = width * 0.25 - shelfW / 2;
  const shelfOuter = width * 0.25 + shelfW / 2;
  const gaps: Array<[number, number]> = [
    [0, shelfInner * 2 - 0.3],
    [-(shelfOuter + width / 2) / 2, width / 2 - shelfOuter - 0.3],
    [(shelfOuter + width / 2) / 2, width / 2 - shelfOuter - 0.3],
  ];

  /**
   * Narrow and dim, because emissive AREA is what blows out, not intensity.
   *
   * The first version was 1.5m wide, 1.66m tall and driven at 0.26, and it read
   * as three sheets of blank printer paper — a big flat quad has no shape to
   * lose, so AgX just clips the whole thing to near-white. Halving the width and
   * halving the drive again turns the same object into a slot of warm light with
   * a silhouette standing in it, which is the only reason it was worth building.
   */
  for (const [x, gapW] of gaps) {
    const nicheW = Math.min(0.95, gapW);
    if (nicheW < 0.5) continue;

    const backlight = new Mesh(
      new PlaneGeometry(nicheW, 1.4),
      emitter('nichePanel', EMIT.shopWarm, 0.12),
    );
    backlight.position.set(x, 1.9, -depth + 0.06);
    backlight.name = 'nichePanel';
    g.add(backlight);

    // Shallow reveal: at 0.22 deep the surround shadowed its own panel from
    // anywhere but dead ahead.
    const surroundMat = MATERIALS.timber();
    g.add(at(box(nicheW + 0.16, 0.08, 0.12, surroundMat, false), x, 2.64, -depth + 0.1));
    g.add(at(box(nicheW + 0.16, 0.08, 0.12, surroundMat, false), x, 1.16, -depth + 0.1));
    for (const s of [-1, 1]) {
      g.add(at(box(0.08, 1.56, 0.12, surroundMat, false), x + s * (nicheW / 2 + 0.04), 1.9, -depth + 0.1));
    }

    // A necklace bust in each, sized to actually fill the slot — the point of
    // a lightbox is the shape standing in front of it.
    const bust = new Mesh(new CylinderGeometry(0.05, 0.2, 0.72, 8), MATERIALS.plasterDim());
    bust.position.set(x, 1.72, -depth + 0.28);
    g.add(bust);
    const chain = new Mesh(
      new TorusGeometry(0.11, 0.01, 4, 14),
      emitter('gold', new Color().setHex(0xffd98a, 'srgb'), 1.4),
    );
    chain.position.set(x, 1.86, -depth + 0.37);
    g.add(chain);
  }

  /**
   * Ring towers — vertical, so the room has something above waist height that is
   * not a wall.
   *
   * These were authored at y = 0.82 on the assumption there was a centre island
   * under them. There is not: the three case runs are a horseshoe around the
   * walls and the middle of the floor is empty, so the discs hung in mid-air and
   * read as a stack of pancakes floating in a black room — one of the two things
   * that made this the worst-looking shot in the building. Each tower now stands
   * on its own pedestal, which is what a jeweller's ring display actually is.
   */
  for (const sx of [-1.5, 1.5]) {
    const tower = new Group();
    tower.position.set(sx, 0, -depth / 2 - 0.4);
    const stem = new Mesh(
      applyContactShading(new CylinderGeometry(0.13, 0.22, 0.82, 10), { falloff: 0.6, strength: 0.45 }),
      MATERIALS.plasterDim(),
    );
    stem.position.y = 0.41;
    tower.add(stem);
    for (let i = 0; i < 4; i++) {
      const disc = new Mesh(
        new CylinderGeometry(0.16 - i * 0.03, 0.16 - i * 0.03, 0.04, 10),
        MATERIALS.plaster(),
      );
      disc.position.y = 0.82 + i * 0.16;
      tower.add(disc);
    }
    g.add(tower);
  }

  /**
   * Two spots, and only two — everything else in here is emissive.
   *
   * Both are hung LOW, at roughly display height rather than at the ceiling.
   * The first pass put one at `height - 0.8` and it lit the suspended tiles and
   * the back wall into a warm tan haze, which is the one thing an ink-black
   * jeweller cannot have: the whole scheme depends on the cases being the only
   * bright objects in the room. Down here the light grazes the glass and dies
   * before it reaches anything with a large flat surface.
   */
  const spot = accent(EMIT.shopWarm, 3.4, depth * 0.75);
  spot.position.set(0, 1.75, -depth + 1.6);
  g.add(spot);
  const front = accent(EMIT.shopWarm, 2.6, depth * 0.6);
  front.position.set(0, 1.6, -depth * 0.42);
  g.add(front);
  g.add(at(lightPool(2.4, EMIT.shopWarm, 0.3, 'guns'), 0, 0.02, -depth + 1.2));

  const sign = wallSign('ETERNAL YOU', width * 0.5, 0.5, EMIT.signAmber, 'wide');
  sign.position.set(0, 2.95, -depth + 0.14);
  g.add(sign);

  return g;
}

// ---------------------------------------------------------------------------
// LIFE SUPPLIES — pharmacy.
// ---------------------------------------------------------------------------

function dressPharmacy(width: number, depth: number, height: number): Group {
  const g = new Group();

  // Dispensary: a raised counter across the back with a security grille above
  // it. The grille is the visual promise that the good stuff is behind glass —
  // the pharmacy is a pick-lock store and the room should say so before you
  // walk in and find out.
  const counter = box(width * 0.62, 1.12, 0.7, MATERIALS.plaster());
  counter.position.set(0, 0.56, -depth + 0.9);
  g.add(counter);

  const grilleMat = MATERIALS.steelDark();
  for (let i = 0; i * 0.16 < width * 0.62; i++) {
    const bar = box(0.03, 1.5, 0.03, grilleMat, false);
    bar.position.set(-width * 0.31 + i * 0.16, 1.9, -depth + 0.62);
    g.add(bar);
  }

  // Back-of-counter apothecary wall: hundreds of identical white boxes, which is
  // exactly what a pharmacy back wall looks like and reads as institutional.
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 14; c++) {
      const b = card(0.2, 0.22, 0.16, r % 2 ? 0xd8d4c8 : 0xc4cfc0);
      b.position.set(-width * 0.3 + c * 0.44, 1.35 + r * 0.3, -depth + 0.22);
      g.add(b);
    }
  }

  // Two open gondola aisles of consumer product running front-to-back.
  for (const sx of [-1, 1]) {
    for (let i = 0; i < 4; i++) {
      const shelf = box(1.1, 0.06, depth * 0.4, MATERIALS.plaster(), false);
      shelf.position.set(sx * width * 0.28, 0.5 + i * 0.42, -depth * 0.52);
      g.add(shelf);
      for (let j = 0; j < 7; j++) {
        const p = card(0.13, 0.24, 0.13, [0xb9d0c4, 0xd6c9a8, 0xc9b6c4, 0xa8bcd0][(i + j) % 4]);
        p.position.set(
          sx * width * 0.28 + ((j % 2) - 0.5) * 0.4,
          0.65 + i * 0.42,
          -depth * 0.52 - depth * 0.18 + j * (depth * 0.055),
        );
        g.add(p);
      }
    }
    const upright = box(0.08, 2.2, depth * 0.4, MATERIALS.steel(), false);
    upright.position.set(sx * width * 0.28, 1.1, -depth * 0.52);
    g.add(upright);
  }

  // Green cross. Clinical, not warm — the one saturated thing in the unit.
  const cross = new Group();
  const crossMat = emitter('pharmaCross', EMIT.exitGreen, 1.5);
  cross.add(new Mesh(new BoxGeometry(0.7, 0.22, 0.05), crossMat));
  cross.add(new Mesh(new BoxGeometry(0.22, 0.7, 0.05), crossMat));
  cross.position.set(width * 0.34, 2.3, -depth + 0.12);
  g.add(cross);

  const l = accent(EMIT.fluoro, 6, depth * 1.3);
  l.position.set(0, height - 0.7, -depth * 0.55);
  g.add(l);

  return g;
}

// ---------------------------------------------------------------------------
// CIRCUIT PLANET — electronics.
// ---------------------------------------------------------------------------

function dressElectronics(width: number, depth: number, height: number): Group {
  const g = new Group();

  /**
   * A wall of televisions all showing the same thing. This is the single most
   * recognisable electronics-store image there is, and it doubles as the room's
   * only light source, which puts a cold flicker on everything — the opposite
   * of the warm shop spill every other unit has.
   */
  const cols = Math.max(4, Math.floor((width - 2) / 1.1));
  const rows = 3;
  // Three shared textures, dealt out across the grid. Sixteen unique canvases
  // would cost sixteen texture uploads for a wall nobody stands closer than
  // three metres to; three is enough for the eye to stop finding the pattern.
  const screenMats = [0, 1, 2].map(
    (i) =>
      new MeshBasicMaterial({ map: makeTvTexture(i), toneMapped: true, fog: true }),
  );
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const bezel = box(1.0, 0.62, 0.09, MATERIALS.rubber(), false);
      const x = -((cols - 1) * 1.08) / 2 + c * 1.08;
      const y = 1.0 + r * 0.72;
      bezel.position.set(x, y, -depth + 0.16);
      g.add(bezel);
      const scr = new Mesh(new PlaneGeometry(0.9, 0.52), screenMats[(r * cols + c * 2) % 3]);
      scr.position.set(x, y, -depth + 0.215);
      g.add(scr);
    }
  }

  // Plinths of laptops, angled open. Low, so the TV wall stays the hero.
  for (let i = 0; i < 4; i++) {
    const px = -width * 0.3 + (i * width * 0.6) / 3;
    const plinth = box(0.9, 0.82, 0.7, MATERIALS.plaster());
    plinth.position.set(px, 0.41, -depth * 0.45);
    g.add(plinth);

    const base = box(0.36, 0.02, 0.26, MATERIALS.rubber(), false);
    base.position.set(px, 0.83, -depth * 0.45);
    g.add(base);
    const lid = box(0.36, 0.24, 0.02, MATERIALS.rubber(), false);
    lid.position.set(px, 0.95, -depth * 0.45 - 0.12);
    lid.rotation.x = 0.35;
    g.add(lid);
    const lidScreen = new Mesh(new PlaneGeometry(0.32, 0.2), emitter('laptopScr', EMIT.lcd, 0.8));
    lidScreen.position.set(px, 0.95, -depth * 0.45 - 0.1);
    lidScreen.rotation.x = 0.35;
    g.add(lidScreen);
  }

  // One drone hanging on fishing line. Nothing else in the mall is suspended in
  // mid-air, so it is worth the four triangles for the silhouette alone.
  const drone = new Group();
  drone.position.set(width * 0.28, height - 1.5, -depth * 0.35);
  drone.add(box(0.3, 0.08, 0.3, MATERIALS.rubber(), false));
  for (const [ax, az] of [
    [-0.24, -0.24],
    [0.24, -0.24],
    [-0.24, 0.24],
    [0.24, 0.24],
  ]) {
    const arm = box(0.24, 0.03, 0.05, MATERIALS.rubber(), false);
    arm.position.set(ax / 2, 0, az / 2);
    arm.rotation.y = Math.atan2(az, ax);
    drone.add(arm);
    const rotor = new Mesh(new CylinderGeometry(0.12, 0.12, 0.01, 8), MATERIALS.steelDark());
    rotor.position.set(ax, 0.03, az);
    drone.add(rotor);
  }
  const line = box(0.006, 1.4, 0.006, MATERIALS.steelDark(), false);
  line.position.set(width * 0.28, height - 0.8, -depth * 0.35);
  g.add(line, drone);

  const l = accent(EMIT.crt, 6, depth * 1.4);
  l.position.set(0, 1.9, -depth + 1.2);
  g.add(l);

  return g;
}

// ---------------------------------------------------------------------------
// LIBERTY OUTFITTERS — guns.
// ---------------------------------------------------------------------------

function dressGuns(width: number, depth: number, height: number): Group {
  const g = new Group();

  // Long guns racked vertically behind a grille. Silhouette does all the work:
  // twenty thin verticals in a row reads as "gun shop" instantly and needs no
  // detail at all beyond a stock, a barrel and a scope bump.
  const rackW = width * 0.7;
  const backer = box(rackW, 2.0, 0.12, MATERIALS.timber());
  backer.position.set(0, 1.5, -depth + 0.1);
  g.add(backer);

  const n = Math.floor(rackW / 0.34);
  for (let i = 0; i < n; i++) {
    const x = -rackW / 2 + 0.17 + i * 0.34;
    const barrel = box(0.035, 0.72, 0.035, MATERIALS.steelDark(), false);
    barrel.position.set(x, 2.06, -depth + 0.26);
    g.add(barrel);
    const stock = box(0.09, 0.66, 0.08, MATERIALS.timber(), false);
    stock.position.set(x, 1.36, -depth + 0.26);
    g.add(stock);
    if (i % 3 === 0) {
      const scope = box(0.05, 0.05, 0.22, MATERIALS.steelDark(), false);
      scope.position.set(x, 1.8, -depth + 0.34);
      g.add(scope);
    }
  }
  for (let i = 0; i * 0.22 <= rackW; i++) {
    const bar = box(0.025, 2.0, 0.025, MATERIALS.steel(), false);
    bar.position.set(-rackW / 2 + i * 0.22, 1.5, -depth + 0.42);
    g.add(bar);
  }

  // Ammunition, stacked. Heavy, low value per kilo — the encumbrance trap.
  for (let i = 0; i < 9; i++) {
    const c = card(0.42, 0.2, 0.3, i % 2 ? 0x5f6a4e : 0x4c553f);
    c.position.set(width * 0.32 + ((i % 3) - 1) * 0.02, 0.1 + Math.floor(i / 3) * 0.21, -depth * 0.35 - (i % 3) * 0.32);
    g.add(c);
  }

  // Camouflaged mannequin. The reference sheet's mannequins are pale clay; this
  // one is the exception, and it earns it by being the only human-shaped thing
  // in the store you can mistake for a guard at a distance.
  const dummy = mannequin('armsOut');
  dummy.position.set(-width * 0.34, 0, -depth * 0.4);
  dummy.rotation.y = 0.5;
  dummy.traverse((o) => {
    const m = (o as Mesh).material as { color?: Color } | undefined;
    if (m?.color) (o as Mesh).material = MATERIALS.foliage();
  });
  g.add(dummy);

  const sign = wallSign('MEMBERS ONLY BEYOND THIS POINT', width * 0.44, 0.3, EMIT.signAmber);
  sign.position.set(0, 2.72, -depth + 0.2);
  g.add(sign);

  const l = accent(EMIT.shopWarm, 6, depth * 1.3);
  l.position.set(0, height - 0.8, -depth * 0.5);
  g.add(l);

  return g;
}

// ---------------------------------------------------------------------------
// MEDIEVAL WEAPONS — the joke store, played completely straight.
// ---------------------------------------------------------------------------

function dressMedieval(width: number, depth: number, height: number): Group {
  const g = new Group();

  // Swords fanned into a starburst on the back wall. Absurd, and exactly what
  // every real mall sword shop does.
  const bladeMat = MATERIALS.steel();
  const hiltMat = MATERIALS.timber();
  for (let i = 0; i < 9; i++) {
    const a = (i / 8 - 0.5) * Math.PI * 0.85;
    const sword = new Group();
    sword.position.set(0, 2.0, -depth + 0.14);
    sword.rotation.z = a;
    const blade = box(0.05, 1.0, 0.014, bladeMat, false);
    blade.position.y = 0.62;
    sword.add(blade);
    const guard = box(0.26, 0.05, 0.03, hiltMat, false);
    guard.position.y = 0.1;
    sword.add(guard);
    const grip = box(0.045, 0.22, 0.045, hiltMat, false);
    grip.position.y = -0.03;
    sword.add(grip);
    g.add(sword);
  }

  /**
   * Suit of armour on a stand. The store's mascot and the one silhouette a
   * thief will remember the room by — but it belongs at the BACK of the unit,
   * not in the middle of it.
   *
   * At 45% depth it stood a metre and a half inside the entrance, which put a
   * 1.9m figure directly in the face of anyone walking in and hid the whole
   * room behind it. Set against the back wall it does the opposite job: it
   * silhouettes against the sword fan and pulls you down the length of the shop.
   */
  const armour = new Group();
  armour.position.set(0, 0, -depth * 0.72);
  /**
   * Built out of faceted cylinders rather than boxes. The first pass used a flat
   * 0.5 × 0.62 box for the cuirass and under a key light it rendered as one
   * uniform cream rectangle — a big flat face pointed at the camera has a single
   * normal, so it takes a single specular value and clips. An eight-sided
   * cylinder gets eight, which is the entire difference between "plate armour"
   * and "sheet of paper".
   *
   * Plate is also `steelDark` throughout. Full `steel` is 0.72 metalness and blew
   * out against the environment map before any lamp touched it.
   */
  /**
   * Not `steel`, and not `steelDark` either. Both are ~0.7 metalness, and a
   * metal has no diffuse term at all — in a room this dark it renders as a
   * perfect black cutout with a couple of specular glints, which is exactly what
   * the second pass showed. Dropping metalness to 0.3 gives the plate something
   * to catch the fill light with while still reading as polished, not painted.
   */
  const plate = surface('armourPlate', SURFACE.steel, { roughness: 0.5, metalness: 0.3 });
  const plinth = new Mesh(new CylinderGeometry(0.42, 0.5, 0.22, 8), MATERIALS.plasterDim());
  plinth.position.y = 0.11;
  armour.add(plinth);

  const cuirass = new Mesh(new CylinderGeometry(0.27, 0.23, 0.6, 8), plate);
  cuirass.position.y = 1.42;
  armour.add(cuirass);
  const faulds = new Mesh(new CylinderGeometry(0.24, 0.3, 0.3, 8), plate);
  faulds.position.y = 1.0;
  armour.add(faulds);
  for (const s of [-1, 1]) {
    const pauldron = new Mesh(new SphereGeometry(0.16, 7, 5), plate);
    pauldron.position.set(s * 0.29, 1.66, 0);
    armour.add(pauldron);
    const arm = new Mesh(new CylinderGeometry(0.075, 0.06, 0.62, 6), plate);
    arm.position.set(s * 0.31, 1.28, 0.02);
    armour.add(arm);
    const gauntlet = new Mesh(new SphereGeometry(0.075, 6, 4), plate);
    gauntlet.position.set(s * 0.31, 0.96, 0.02);
    armour.add(gauntlet);
    const leg = new Mesh(new CylinderGeometry(0.1, 0.08, 0.64, 6), plate);
    leg.position.set(s * 0.13, 0.54, 0);
    armour.add(leg);
    const boot = box(0.16, 0.1, 0.28, plate, false);
    boot.position.set(s * 0.13, 0.27, 0.05);
    armour.add(boot);
  }
  const gorget = new Mesh(new CylinderGeometry(0.13, 0.17, 0.1, 8), plate);
  gorget.position.y = 1.77;
  armour.add(gorget);
  const helm = new Mesh(new CylinderGeometry(0.145, 0.165, 0.32, 8), plate);
  helm.position.y = 1.98;
  armour.add(helm);
  const crest = box(0.03, 0.16, 0.26, MATERIALS.timber(), false);
  crest.position.set(0, 2.2, -0.02);
  armour.add(crest);
  // Visor slot. Unlit black, so it stays a hole no matter what the room does.
  const visor = new Mesh(new PlaneGeometry(0.2, 0.05), new MeshBasicMaterial({ color: 0x000000 }));
  visor.position.set(0, 2.0, 0.155);
  armour.add(visor);
  // Sword grounded point-down in front, both hands on the pommel.
  const propBlade = box(0.05, 0.95, 0.014, MATERIALS.steel(), false);
  propBlade.position.set(0, 0.7, 0.22);
  armour.add(propBlade);
  const propGuard = box(0.28, 0.05, 0.03, MATERIALS.timber(), false);
  propGuard.position.set(0, 1.2, 0.22);
  armour.add(propGuard);
  const propGrip = box(0.045, 0.2, 0.045, MATERIALS.timber(), false);
  propGrip.position.set(0, 1.32, 0.22);
  armour.add(propGrip);
  armour.traverse((o) => {
    if ((o as Mesh).isMesh) (o as Mesh).castShadow = true;
  });
  g.add(armour);

  // Halberds leaned in a barrel, and a shield pair. Cheap, and they fill the
  // corners that the wall starburst does not reach.
  const barrel = new Mesh(new CylinderGeometry(0.34, 0.3, 0.7, 10), MATERIALS.timber());
  barrel.position.set(width * 0.33, 0.35, -depth * 0.55);
  g.add(barrel);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const haft = box(0.05, 2.1, 0.05, hiltMat, false);
    haft.position.set(width * 0.33 + Math.cos(a) * 0.16, 1.1, -depth * 0.55 + Math.sin(a) * 0.16);
    haft.rotation.set(Math.sin(a) * 0.1, 0, -Math.cos(a) * 0.1);
    g.add(haft);
    const head = box(0.22, 0.3, 0.03, bladeMat, false);
    head.position.set(width * 0.33 + Math.cos(a) * 0.3, 2.1, -depth * 0.55 + Math.sin(a) * 0.3);
    g.add(head);
  }
  for (let i = 0; i < 2; i++) {
    const shield = new Mesh(new CylinderGeometry(0.34, 0.34, 0.05, 6), MATERIALS.timber());
    shield.rotation.x = Math.PI / 2;
    shield.position.set(-width * 0.36, 1.4 + i * 0.85, -depth + 0.15);
    g.add(shield);
    const boss = new Mesh(new SphereGeometry(0.09, 6, 4), MATERIALS.steel());
    boss.position.set(-width * 0.36, 1.4 + i * 0.85, -depth + 0.2);
    g.add(boss);
  }

  // Clear of the left stock shelf, which `placeContainers` parks at -width*0.25
  // and which swallowed this sign at -width*0.3.
  const sign = wallSign('AUTHENTIC REPRODUCTIONS', width * 0.3, 0.26, EMIT.signAmber, 'handwritten');
  sign.position.set(-width * 0.42, 1.1, -depth + 0.16);
  g.add(sign);

  // Room light kept forward of the mascot, plus a low spot that grazes the
  // armour from the front so it reads as plate rather than as a grey cutout.
  const l = accent(EMIT.shopWarm, 6.5, depth * 1.3);
  l.position.set(0, height - 0.8, -depth * 0.4);
  g.add(l);
  const key = accent(EMIT.shopWarm, 7.0, 5.0);
  key.position.set(0, 2.3, -depth * 0.72 + 1.7);
  g.add(key);
  g.add(at(lightPool(2.0, EMIT.shopWarm, 0.16, 'armour'), 0, 0.02, -depth * 0.72));

  return g;
}

// ---------------------------------------------------------------------------
// ORBITAL OUTFITTERS — clothing.
// ---------------------------------------------------------------------------

function dressClothing(width: number, depth: number, height: number): Group {
  const g = new Group();

  /**
   * Circular rails of hanging garments. Every coat is one tapered box, and the
   * variation comes from the palette and a per-item yaw — thirty of them in a
   * ring reads as a dense rack from any distance without a single unique mesh.
   */
  const COAT = [0x4a4f58, 0x6b5c4a, 0x3f4a44, 0x5d5560, 0x74695a];
  const rail = (cx: number, cz: number, radius: number) => {
    const ring = new Mesh(new TorusGeometry(radius, 0.02, 4, 18), MATERIALS.steel());
    ring.rotation.x = Math.PI / 2;
    ring.position.set(cx, 1.62, cz);
    g.add(ring);
    const post = box(0.06, 1.62, 0.06, MATERIALS.steel(), false);
    post.position.set(cx, 0.81, cz);
    g.add(post);
    const foot = new Mesh(new CylinderGeometry(0.3, 0.34, 0.05, 8), MATERIALS.steelDark());
    foot.position.set(cx, 0.03, cz);
    g.add(foot);

    const count = Math.round(radius * 26);
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const coat = card(0.1, 0.9, 0.34, COAT[i % COAT.length]);
      coat.position.set(cx + Math.cos(a) * radius, 1.12, cz + Math.sin(a) * radius);
      coat.rotation.y = -a;
      g.add(coat);
    }
  };
  rail(-width * 0.26, -depth * 0.42, 0.8);
  rail(width * 0.26, -depth * 0.42, 0.8);
  rail(0, -depth * 0.72, 0.62);

  // Folding table of stacked knitwear, and the shoe wall behind it.
  const table = box(1.8, 0.72, 0.9, MATERIALS.timber());
  table.position.set(0, 0.36, -depth * 0.24);
  g.add(table);
  for (let i = 0; i < 6; i++) {
    const stack = card(0.42, 0.06 + (i % 3) * 0.04, 0.42, COAT[(i * 2) % COAT.length]);
    stack.position.set(-0.6 + (i % 3) * 0.6, 0.76, -depth * 0.24 + (i < 3 ? -0.2 : 0.2));
    g.add(stack);
  }
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 10; c++) {
      const shoe = card(0.26, 0.13, 0.14, [0xcfc7b8, 0x3f4650, 0x8a6f52][(r + c) % 3]);
      shoe.position.set(-width * 0.36 + c * 0.3, 0.9 + r * 0.42, -depth + 0.2);
      g.add(shoe);
      const ledge = box(0.3, 0.02, 0.28, MATERIALS.plaster(), false);
      ledge.position.set(-width * 0.36 + c * 0.3, 0.83 + r * 0.42, -depth + 0.2);
      g.add(ledge);
    }
  }

  // Two changing cubicles with the curtains half open. Somewhere to hide, and
  // the game will eventually want exactly that.
  for (const s of [0, 1]) {
    const x = width * 0.32 + s * 0.02;
    const cube = box(1.0, 2.1, 1.0, MATERIALS.plasterDim());
    cube.position.set(x, 1.05, -depth + 0.6 + s * 1.15);
    g.add(cube);
    const curtain = box(0.62, 1.9, 0.04, MATERIALS.rubber(), false);
    curtain.position.set(x - 0.18, 1.0, -depth + 1.11 + s * 1.15);
    g.add(curtain);
  }

  /**
   * The mannequins, which are the store — panel 3 of the reference sheet is a
   * clothing shop and it is entirely carried by pale figures standing in the
   * dark. Four of them, spread across the depth of the unit rather than lined up
   * across the front, so from the doorway they recede and one is always partly
   * behind a rail. Each gets its own floor pool; a mannequin with no light of its
   * own is a grey shape against a grey wall.
   */
  for (const [mx, mz, pose] of [
    [-width * 0.38, -depth * 0.16, 'contrapposto'],
    [width * 0.06, -depth * 0.12, 'stand'],
    [-width * 0.12, -depth * 0.56, 'armsOut'],
    [width * 0.4, -depth * 0.3, 'contrapposto'],
  ] as const) {
    const m = mannequin(pose);
    m.position.set(mx, 0, mz);
    m.rotation.y = Math.PI + mx * 0.06;
    g.add(m);
    g.add(at(lightPool(1.9, EMIT.shopWarm, 0.13, 'mannequin'), mx, 0.02, mz));
  }

  /**
   * Three tight spots instead of one room-wide flood.
   *
   * The unit already carries a generic ceiling fill, so the broad accent that
   * used to live here was a second wash on top of the first, and two overlapping
   * floods is precisely how a room ends up evenly lit and dead. These are short
   * range on purpose: they pick out the two front mannequins and the centre rail
   * and leave the corners to fall away.
   */
  for (const [lx, lz] of [
    [-width * 0.36, -depth * 0.18],
    [width * 0.08, -depth * 0.16],
    [0, -depth * 0.62],
  ] as const) {
    const spot = accent(EMIT.shopWarm, 5.0, 4.6);
    spot.position.set(lx, height - 1.1, lz);
    g.add(spot);
  }

  return g;
}

// ---------------------------------------------------------------------------
// DEPARTMENT OF THINGS — department store.
// ---------------------------------------------------------------------------

function dressDepartment(width: number, depth: number, height: number): Group {
  const g = new Group();

  // Rows of low gondolas. Low on purpose: this is the only unit wide enough to
  // read as a floor rather than a room, and keeping the fittings below eye
  // height is what sells the depth.
  for (let row = 0; row < 3; row++) {
    const z = -depth * 0.3 - row * (depth * 0.22);
    const gond = box(width * 0.72, 0.06, 0.9, MATERIALS.plaster(), false);
    gond.position.set(0, 1.02, z);
    g.add(gond);
    const body = box(width * 0.72, 1.0, 0.9, MATERIALS.plasterDim());
    body.position.set(0, 0.5, z);
    g.add(body);
    const cols = Math.floor(width * 0.68 / 0.42);
    for (let c = 0; c < cols; c++) {
      const p = card(
        0.3,
        0.2 + ((row + c) % 3) * 0.1,
        0.3,
        [0xb8a98c, 0x8d94a0, 0xa9b0a2, 0xc2b0b8][(row * 3 + c) % 4],
      );
      p.position.set(-width * 0.34 + 0.21 + c * 0.42, 1.16, z + ((c % 2) - 0.5) * 0.3);
      g.add(p);
    }
  }

  // Perfume island — glass, mirror and a hundred tiny bottles. The one warm,
  // sparkly thing in an otherwise beige store.
  const island = new Group();
  island.position.set(0, 0, -depth * 0.14);
  const isBody = box(2.6, 0.9, 1.1, MATERIALS.plaster());
  isBody.position.y = 0.45;
  island.add(isBody);
  const isTop = box(2.7, 0.06, 1.2, MATERIALS.glass(), false);
  isTop.position.y = 0.93;
  island.add(isTop);
  for (let i = 0; i < 22; i++) {
    const bottle = new Mesh(
      new BoxGeometry(0.07, 0.13 + (i % 3) * 0.04, 0.07),
      glow(`perfume${i % 3}`, SURFACE.glass, [EMIT.potionA, EMIT.potionB, EMIT.potionC][i % 3], 0.8),
    );
    bottle.position.set(-1.15 + (i % 11) * 0.23, 1.03 + (i % 3) * 0.02, i < 11 ? -0.22 : 0.22);
    island.add(bottle);
  }
  g.add(island);

  // Bank of tills across the front. `till` is a spawn here, so the fittings
  // should show where the cash lives.
  for (let i = 0; i < 3; i++) {
    const x = -width * 0.3 + i * (width * 0.3);
    const till = box(0.42, 0.28, 0.36, MATERIALS.plasterDim());
    till.position.set(x, 1.13, -depth * 0.86);
    g.add(till);
    const desk = box(1.3, 0.98, 0.7, MATERIALS.timber());
    desk.position.set(x, 0.49, -depth * 0.86);
    g.add(desk);
  }

  const sign = wallSign('BUY MORE FOR NO REASON', width * 0.4, 0.34, EMIT.signAmber);
  sign.position.set(0, 2.6, -depth + 0.14);
  g.add(sign);

  const l = accent(EMIT.shopWarm, 7, depth * 1.4);
  l.position.set(0, height - 0.8, -depth * 0.45);
  g.add(l);

  return g;
}

// ---------------------------------------------------------------------------
// NOURISH YOUR NEEDS — supermarket.
// ---------------------------------------------------------------------------

function dressSupermarket(width: number, depth: number, height: number): Group {
  const g = new Group();

  // Checkout lanes across the front, belts and all. A supermarket is unreadable
  // without them — they are the thing that makes the space a supermarket rather
  // than a warehouse.
  const lanes = 3;
  for (let i = 0; i < lanes; i++) {
    const x = -width * 0.3 + (i * width * 0.6) / (lanes - 1);
    const belt = box(0.7, 0.9, 2.2, MATERIALS.plasterDim());
    belt.position.set(x, 0.45, -depth * 0.2);
    g.add(belt);
    const surface = box(0.62, 0.03, 2.1, MATERIALS.rubber(), false);
    surface.position.set(x, 0.92, -depth * 0.2);
    g.add(surface);
    const post = box(0.1, 1.9, 0.1, MATERIALS.steel(), false);
    post.position.set(x + 0.5, 0.95, -depth * 0.2 - 1.0);
    g.add(post);
    const laneNo = new Mesh(
      new PlaneGeometry(0.34, 0.34),
      signMaterial({ text: `${i + 1}`, color: EMIT.signAmber, widthMetres: 0.34, heightMetres: 0.34, align: 'center' }),
    );
    laneNo.position.set(x + 0.5, 1.95, -depth * 0.2 - 0.94);
    g.add(laneNo);
  }

  // Tall aisles behind them, running away from the shopfront so the sightline
  // down each one is a long dark slot — good for hiding in, good for the shot.
  const RACK_LEN = depth * 0.44;
  const RACK_Z = -depth * 0.68;
  const SHELF = [0.42, 0.92, 1.42, 1.92];
  for (let a = 0; a < 4; a++) {
    const x = -width * 0.34 + (a * width * 0.68) / 3;
    const rack = box(0.95, 2.1, RACK_LEN, MATERIALS.steelDark());
    rack.position.set(x, 1.05, RACK_Z);
    g.add(rack);

    /**
     * Shelf boards, and then goods standing ON them.
     *
     * The first pass hung products in mid-air 5cm off the face of a solid dark
     * box, four to a side with nearly a metre of gap between them, and the
     * result down the aisle was a featureless black slab with a few floating
     * cubes stuck to it. A supermarket aisle reads through repetition — a
     * continuous horizontal line of shelf with a dense, slightly irregular run
     * of packaging on it — so the boards are what actually do the work here and
     * the products are just texture along them.
     */
    for (const s of [-1, 1]) {
      const bx = x + s * 0.68;
      for (const y of SHELF) {
        const board = box(0.44, 0.04, RACK_LEN - 0.08, MATERIALS.steel(), false);
        board.position.set(bx, y, RACK_Z);
        g.add(board);
        // Price rail along the lip: a thin bright edge is what makes a dark
        // aisle legible from the far end.
        const rail = box(0.02, 0.06, RACK_LEN - 0.08, MATERIALS.plaster(), false);
        rail.position.set(bx + s * 0.21, y + 0.05, RACK_Z);
        g.add(rail);

        const cols = Math.max(4, Math.round(RACK_LEN / 0.34));
        for (let j = 0; j < cols; j++) {
          const t = (j + 0.5) / cols;
          const seed = a * 31 + j * 7 + Math.round(y * 10);
          const h = 0.2 + (seed % 3) * 0.07;
          const p = card(0.19, h, 0.22, [0xa8b09a, 0xc0a888, 0x98a4b0, 0xb09a9a][seed % 4]);
          p.position.set(
            bx - s * 0.02,
            y + 0.02 + h / 2,
            RACK_Z - RACK_LEN / 2 + 0.06 + t * (RACK_LEN - 0.12),
          );
          g.add(p);
        }
      }
    }

    // End cap: a lit header board, so the aisle ends in a sign rather than in a
    // black rectangle, and the shopper has something to read down the row.
    const header = box(0.99, 0.3, 0.06, MATERIALS.plaster(), false);
    header.position.set(x, 2.28, RACK_Z + RACK_LEN / 2);
    g.add(header);
    const num = new Mesh(
      new PlaneGeometry(0.8, 0.24),
      signMaterial({
        text: ['PANTRY', 'CHILLED', 'HOUSEHOLD', 'SNACKS'][a],
        color: EMIT.signAmber,
        widthMetres: 0.8,
        heightMetres: 0.24,
        align: 'center',
      }),
    );
    num.position.set(x, 2.28, RACK_Z + RACK_LEN / 2 + 0.04);
    g.add(num);
  }

  // Chest freezers. Cold light from below is the one cool accent in the unit
  // and it separates the back of the room from the warm front.
  for (const fx of [-width * 0.36, width * 0.36]) {
    const chest = box(1.3, 0.95, 2.4, MATERIALS.plaster());
    chest.position.set(fx, 0.48, -depth * 0.5);
    g.add(chest);
    const lid = new Mesh(
      new PlaneGeometry(1.2, 2.3),
      glow('freezer', SURFACE.glass, EMIT.fluoro, 0.9),
    );
    lid.rotation.x = -Math.PI / 2;
    lid.position.set(fx, 0.97, -depth * 0.5);
    g.add(lid);
  }

  // Nested trolleys by the door. Pure silhouette, and instantly legible.
  for (let i = 0; i < 5; i++) {
    const t = new Group();
    t.position.set(width * 0.42, 0, -1.2 - i * 0.34);
    const basket = box(0.52, 0.4, 0.72, MATERIALS.steel(), false);
    basket.position.y = 0.62;
    basket.rotation.x = 0.12;
    t.add(basket);
    const handle = box(0.5, 0.03, 0.03, MATERIALS.rubber(), false);
    handle.position.set(0, 0.96, 0.36);
    t.add(handle);
    for (const [wx, wz] of [[-0.2, -0.28], [0.2, -0.28], [-0.2, 0.28], [0.2, 0.28]]) {
      const wheel = new Mesh(new CylinderGeometry(0.06, 0.06, 0.04, 6), MATERIALS.rubber());
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(wx, 0.06, wz);
      t.add(wheel);
    }
    g.add(t);
  }

  const l = accent(EMIT.fluoro, 7, depth * 1.5);
  l.position.set(0, height - 0.7, -depth * 0.5);
  g.add(l);

  return g;
}

// ---------------------------------------------------------------------------
// THE EATING PLACE — food court counter.
// ---------------------------------------------------------------------------

function dressFoodcourt(width: number, depth: number, height: number): Group {
  const g = new Group();

  // Serving counter with a sneeze guard and heat lamps. The lamps are the only
  // orange point sources inside the building and they make the one still-lit
  // unit at the dead end of the food court read from forty metres away.
  const counter = box(width * 0.78, 1.1, 0.85, MATERIALS.plaster());
  counter.position.set(0, 0.55, -depth * 0.35);
  g.add(counter);
  const guard = box(width * 0.78, 0.5, 0.03, MATERIALS.glass(), false);
  guard.position.set(0, 1.45, -depth * 0.35 + 0.4);
  guard.castShadow = false;
  g.add(guard);

  for (let i = 0; i < 4; i++) {
    const x = -width * 0.28 + (i * width * 0.56) / 3;
    const tray = box(0.7, 0.09, 0.55, MATERIALS.steel(), false);
    tray.position.set(x, 1.15, -depth * 0.35);
    g.add(tray);
    const lampBar = box(0.6, 0.05, 0.06, emitter('heatLamp', EMIT.sodium, 1.4), false);
    lampBar.position.set(x, 1.78, -depth * 0.35);
    g.add(lampBar);
    const hanger = box(0.02, 0.5, 0.02, MATERIALS.steelDark(), false);
    hanger.position.set(x, 2.03, -depth * 0.35);
    g.add(hanger);
  }

  // Menu board. Deadpan: the prices are the joke.
  const MENU = ['ITEM ................ 8', 'LARGE ITEM .......... 12', 'ITEM COMBINATION .... 15', 'DRINK ............... 4'];
  MENU.forEach((line, i) => {
    const m = new Mesh(
      new PlaneGeometry(width * 0.4, 0.24),
      signMaterial({
        text: line,
        color: EMIT.signAmber,
        widthMetres: width * 0.4,
        heightMetres: 0.24,
        align: 'left',
      }),
    );
    m.position.set(-width * 0.14, 2.45 - i * 0.28, -depth + 0.14);
    g.add(m);
  });
  const board = box(width * 0.5, 1.3, 0.06, MATERIALS.rubber(), false);
  board.position.set(-width * 0.14, 2.1, -depth + 0.1);
  g.add(board);

  // Espresso machines and a stack of trays — the actual loot, on show.
  for (let i = 0; i < 3; i++) {
    const em = box(0.5, 0.42, 0.42, MATERIALS.steel());
    em.position.set(width * 0.24 + (i % 2) * 0.05, 1.31, -depth * 0.35 - (i - 1) * 0.55);
    g.add(em);
  }
  for (let i = 0; i < 9; i++) {
    const tr = box(0.45, 0.03, 0.34, MATERIALS.timber(), false);
    tr.position.set(-width * 0.36, 1.13 + i * 0.035, -depth * 0.35);
    g.add(tr);
  }

  const l = accent(EMIT.shopWarm, 9, depth * 1.8);
  l.position.set(0, height - 0.7, -depth * 0.4);
  g.add(l);
  g.add(at(lightPool(2.8, EMIT.shopWarm, 0.22, 'skate'), 0, 0.02, -depth * 0.2));

  return g;
}

// ---------------------------------------------------------------------------
// CONCRETE WAVE — skate shop.
// ---------------------------------------------------------------------------

function dressSkate(width: number, depth: number, height: number): Group {
  const g = new Group();

  // Deck wall. Boards mounted vertically in a grid is the entire visual identity
  // of a skate shop, and each one is a single tapered box.
  const DECK = [0x8a4a3c, 0x3c5a7a, 0x4d6b45, 0x7a6a3c, 0x6b3c6a];
  const cols = Math.floor((width - 1.4) / 0.34);
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < cols; c++) {
      const deck = card(0.24, 0.82, 0.03, DECK[(r * cols + c) % DECK.length]);
      deck.position.set(-((cols - 1) * 0.34) / 2 + c * 0.34, 1.25 + r * 0.95, -depth + 0.12);
      g.add(deck);
      for (const ty of [-0.28, 0.28]) {
        const truck = box(0.14, 0.04, 0.07, MATERIALS.steel(), false);
        truck.position.set(-((cols - 1) * 0.34) / 2 + c * 0.34, 1.25 + r * 0.95 + ty, -depth + 0.16);
        g.add(truck);
      }
    }
  }

  /**
   * A quarter-pipe in the middle of the sales floor. It is absurd, it is what
   * these shops actually do, and mechanically it is a ramp a thief can run up to
   * reach the mezzanine ductwork — set dressing that might survive into design.
   */
  const ramp = new Group();
  ramp.position.set(width * 0.22, 0, -depth * 0.55);
  const SEGS = 9;
  const RAD = 2.0;
  for (let i = 0; i < SEGS; i++) {
    const a0 = (i / SEGS) * (Math.PI / 2);
    const a1 = ((i + 1) / SEGS) * (Math.PI / 2);
    const mid = (a0 + a1) / 2;
    const seg = box(2.6, 0.12, (RAD * Math.PI) / 2 / SEGS + 0.04, MATERIALS.timber(), false);
    seg.rotation.x = -mid;
    seg.position.set(0, RAD - Math.cos(mid) * RAD, -Math.sin(mid) * RAD);
    ramp.add(seg);
  }
  const coping = new Mesh(new CylinderGeometry(0.05, 0.05, 2.6, 8), MATERIALS.steel());
  coping.rotation.z = Math.PI / 2;
  coping.position.set(0, RAD, -RAD);
  ramp.add(coping);
  for (const s of [-1.3, 1.3]) {
    const cheek = box(0.08, RAD, RAD, MATERIALS.timber(), false);
    cheek.position.set(s, RAD / 2, -RAD / 2);
    ramp.add(cheek);
  }
  g.add(ramp);

  // Shoe wall and a bin of loose wheels.
  for (let i = 0; i < 8; i++) {
    const shoe = card(0.26, 0.14, 0.15, [0xcfc7b8, 0x2f3640, 0x9a7050][i % 3]);
    shoe.position.set(-width * 0.38, 0.7 + i * 0.3, -depth * 0.7 + (i % 2) * 0.34);
    g.add(shoe);
  }
  const bin = new Mesh(new CylinderGeometry(0.4, 0.34, 0.5, 8), MATERIALS.rubber());
  bin.position.set(-width * 0.3, 0.25, -depth * 0.25);
  g.add(bin);

  const sign = wallSign('NO SKATING IN THE MALL', width * 0.38, 0.3, EMIT.signMagenta, 'handwritten');
  sign.position.set(0, 0.7, -depth + 0.16);
  g.add(sign);

  const l = accent(EMIT.signMagenta, 5, depth);
  l.position.set(0, height - 1.0, -depth * 0.6);
  g.add(l);

  return g;
}

// ---------------------------------------------------------------------------
// Interior schemes
// ---------------------------------------------------------------------------

/**
 * The shell each store's fittings sit inside.
 *
 * Dressing alone was not enough. With every unit built from the same pale
 * plaster walls, pale tile floor and one warm ceiling light, ten stores with
 * completely different fittings still read as ten identical beige boxes — the
 * furniture changed and the room did not. The wall, the floor and the colour of
 * the fill are doing more work for store identity than anything on the shelves.
 *
 * `fill` scales the generic ceiling light plan.ts hangs in every lit unit.
 * Turning it down is not a lighting bug: a jeweller lit only from inside its own
 * cases is the whole point, and rule 2 of the art direction says light is pooled.
 */
export interface StoreScheme {
  wall: () => Material;
  floor: () => Material;
  /** Multiplier on the unit's generic ceiling fill. 0 leaves the room to its own fittings. */
  fill: number;
  /** Overrides the fill colour. Defaults to the warm shop spill. */
  fillColor?: Color;
}

const ink = () => surface('wallInk', new Color().setHex(0x2a2622, 'srgb'), { roughness: 0.9 });
const slate = () => surface('wallSlate', new Color().setHex(0x35393f, 'srgb'), { roughness: 0.88 });
const stone = () => surface('wallStone', new Color().setHex(0x5c5a54, 'srgb'), { roughness: 0.96 });
const oxblood = () => surface('wallOxblood', new Color().setHex(0x43302c, 'srgb'), { roughness: 0.93 });
/**
 * The cold white rooms — pharmacy and supermarket. Still the palest walls in the
 * mall, but dropped from 0xa8b2ac for the same reason as `cream`: these are the
 * two units running the ceiling rig at full strength, so they have the least
 * headroom of anywhere before the wall clips and the shelving in front of it
 * stops reading.
 */
const clinic = () => surface('wallClinic', new Color().setHex(0x7f8a85, 'srgb'), { roughness: 0.86 });
/**
 * Warm neutral, and much darker than it wants to be.
 *
 * This started at 0x9e9384, which is a perfectly reasonable paint colour and a
 * disastrous albedo: at 62% reflectance, with a ceiling rig above it and the
 * environment map behind it, a whole wall of it lands near white and the room
 * loses every silhouette in front of it. Clothing rendered as a milk box with
 * the mannequins invisible against their own backdrop. The rule the reference
 * sheet keeps proving is that interior albedos have to sit far lower than the
 * real-world colour so the LIGHT is what varies across a surface, not the paint.
 */
const cream = () => surface('wallCream', new Color().setHex(0x6b6154, 'srgb'), { roughness: 0.92 });

const SCHEMES: Record<StoreId, StoreScheme> = {
  // Black box, gold light. The most expensive room in the mall should also be
  // the darkest, so the cases are the only thing your eye can land on.
  jewellery: { wall: ink, floor: () => MATERIALS.rubber(), fill: 0 },
  // Hospital-adjacent: cold, bright, and the only genuinely white interior.
  pharmacy: { wall: clinic, floor: () => MATERIALS.tile(), fill: 1.0, fillColor: EMIT.fluoro },
  // Dark, so the television wall is the light source rather than competing with one.
  electronics: { wall: slate, floor: () => MATERIALS.concreteDark(), fill: 0.2, fillColor: EMIT.crt },
  guns: { wall: oxblood, floor: () => MATERIALS.timber(), fill: 0.65 },
  medieval: { wall: stone, floor: () => MATERIALS.concreteDark(), fill: 0.7 },
  // The room the mannequins have to read in, so the generic rig is turned down
  // and the dresser's own spots do the work. Pale wall AND pale floor AND a
  // full-strength ceiling grid made this the one unit that looked lit rather
  // than abandoned — milky cream in a moonlit mall.
  clothing: { wall: cream, floor: () => MATERIALS.tileDark(), fill: 0.5 },
  department: { wall: cream, floor: () => MATERIALS.tileDark(), fill: 0.95 },
  supermarket: { wall: clinic, floor: () => MATERIALS.tile(), fill: 1.15, fillColor: EMIT.fluoro },
  foodcourt: { wall: oxblood, floor: () => MATERIALS.tileDark(), fill: 1.1 },
  skate: { wall: slate, floor: () => MATERIALS.concrete(), fill: 0.45 },
};

const DEFAULT_SCHEME: StoreScheme = {
  wall: () => MATERIALS.plasterDim(),
  floor: () => MATERIALS.tileDark(),
  fill: 1,
};

/** The shell for a unit. Vacant units pass null and get the default grey box. */
export function schemeFor(id: StoreId | null): StoreScheme {
  return (id && SCHEMES[id]) || DEFAULT_SCHEME;
}

// ---------------------------------------------------------------------------

const DRESSERS: Record<StoreId, (w: number, d: number, h: number) => Group> = {
  jewellery: dressJewellery,
  pharmacy: dressPharmacy,
  electronics: dressElectronics,
  guns: dressGuns,
  medieval: dressMedieval,
  clothing: dressClothing,
  department: dressDepartment,
  supermarket: dressSupermarket,
  foodcourt: dressFoodcourt,
  skate: dressSkate,
};

/**
 * Build the non-interactive interior for a store, in the unit's local frame.
 *
 * Returns an empty group for a store with no dresser rather than throwing, so
 * adding a store to the sim never breaks the level build — it just ships bare
 * until someone furnishes it.
 */
export function dressStore(id: StoreId, width: number, depth: number, height: number): Group {
  const fn = DRESSERS[id];
  if (!fn) return new Group();
  const g = fn(width, depth, height);
  g.name = `dress_${id}`;
  return g;
}
